import React, { useCallback, useState } from 'react';
import { TableRow } from '@amzn/meridian/table';
import { SortableColumn, SortableDirection } from '../TabHelper';

interface ExpandableRowProps {
  rowCount: number;
  rowComponents: Record<string, any>[] | undefined;
  currentPage: number;
  currentSortColumn: SortableColumn;
  currentSortDirection: SortableDirection;
  isExpandable: boolean;
  children: React.ReactNode;
}

const ExpandableRow: React.FC<ExpandableRowProps> = ({
  rowCount,
  rowComponents,
  currentPage,
  currentSortColumn,
  currentSortDirection,
  isExpandable,
  children,
}: ExpandableRowProps): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [column, setColumn] = useState(SortableColumn.LAST_MODIFIED_DATE);
  const [direction, setDirection] = useState(SortableDirection.DECENDING);

  const onClick = useCallback(() => setOpen(!open), [open]);

  const isPageChanged: boolean = currentPage !== page;
  const isDirectionChanged: boolean = currentSortDirection !== direction;
  const isColumnChanged: boolean = currentSortColumn !== column;

  const handleOpen = (
    opened: boolean,
    pageChanged: boolean,
    directionChanged: boolean,
    columnChanged: boolean
  ): boolean => {
    if (pageChanged && opened) {
      setOpen(false);
      setPage(currentPage);
      return !opened;
    }

    if (directionChanged && opened) {
      setOpen(false);
      setDirection(currentSortDirection);
      return !opened;
    }

    if (columnChanged && opened) {
      setOpen(false);
      setColumn(currentSortColumn);
      return !opened;
    }

    return opened;
  };

  return isExpandable ? (
    <TableRow
      open={handleOpen(
        open,
        isPageChanged,
        isDirectionChanged,
        isColumnChanged
      )}
      onClick={onClick}
      rowComponents={rowComponents}
      highlightOnHover={false}
      backgroundColor={rowCount % 2 !== 0 ? 'secondary' : 'primary'}
      key={`${currentPage}_${rowCount}`}
    >
      {children}
    </TableRow>
  ) : (
    <TableRow
      highlightOnHover={false}
      backgroundColor={rowCount % 2 !== 0 ? 'secondary' : 'primary'}
      key={`${currentPage}_${rowCount}`}
    >
      {children}
    </TableRow>
  );
};

export default ExpandableRow;
